import React, { useMemo } from 'react'
import { Grid, Box, Typography, Chip, Checkbox, Button } from '@mui/material'
import { DirectionsCar, AccessTime, LocationOn } from '@mui/icons-material'
import { Vehicle } from '@otw/models'
import VehicleCard from '../cards/VehicleCard'

interface ReadyForTransportCardsProps {
  vehicles: Vehicle[]
  onViewVehicle?: (vehicle: Vehicle) => void
  onManagePhotos?: (vehicle: Vehicle) => void
  onSelectionChange?: (selectedVehicles: Vehicle[]) => void
  onUnmarkReadyForPickup?: (vehicle: Vehicle) => void
  selectedVehicleIds?: string[]
}

const ReadyForTransportCards: React.FC<ReadyForTransportCardsProps> = ({
  vehicles,
  onViewVehicle,
  onManagePhotos,
  onSelectionChange,
  onUnmarkReadyForPickup,
  selectedVehicleIds = [],
}) => {
  const handleVehicleToggle = (vehicle: Vehicle, checked: boolean) => {
    if (!onSelectionChange) return

    if (checked) {
      const existing = selectedVehicleIds.includes(vehicle.id)
      if (existing) return
      const selectedVehicles = vehicles.filter((v) =>
        [...selectedVehicleIds, vehicle.id].includes(v.id)
      )
      onSelectionChange(selectedVehicles)
      return
    }

    const selectedVehicles = vehicles.filter((v) =>
      selectedVehicleIds.filter((id) => id !== vehicle.id).includes(v.id)
    )
    onSelectionChange(selectedVehicles)
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
      return dateString
    }
  }

  const formatTimeSince = (dateString: string | null | undefined) => {
    if (!dateString) return null
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

      if (diffDays > 0) {
        return `${diffDays}d ${diffHours}h`
      } else if (diffHours > 0) {
        return `${diffHours}h ${diffMinutes}m`
      } else {
        return `${diffMinutes}m`
      }
    } catch {
      return null
    }
  }

  // Sort vehicles by ready_for_transport_date (oldest first)
  const sortedVehicles = useMemo(() => {
    return [...vehicles].sort((a, b) => {
      const dateA = a.ready_for_transport?.ready_for_transport_date
      const dateB = b.ready_for_transport?.ready_for_transport_date
      
      if (!dateA && !dateB) return 0
      if (!dateA) return 1 // Vehicles without date go to the end
      if (!dateB) return -1
      
      return new Date(dateA).getTime() - new Date(dateB).getTime()
    })
  }, [vehicles])

  if (vehicles.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '400px',
        p: 4
      }}>
        <DirectionsCar sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No vehicles ready for transport
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Vehicles marked as ready for transport will appear here
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {sortedVehicles.map((vehicle) => {
          const readyForTransport = vehicle.ready_for_transport

          const isSelected = selectedVehicleIds.includes(vehicle.id)
          const timeSince = formatTimeSince(vehicle.ready_for_transport?.ready_for_transport_date)
          const pickupLabel = [
            vehicle.pickup_location?.address,
            vehicle.pickup_location?.city,
            vehicle.pickup_location?.state,
            vehicle.pickup_location?.zip,
          ]
            .filter(Boolean)
            .join(', ')

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={vehicle.id} sx={{ display: 'flex' }}>
              <Box sx={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Time Since Chip - Top Left */}
                {timeSince && (
                  <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}>
                    <Chip
                      icon={<AccessTime />}
                      label={timeSince}
                      size="small"
                      color="default"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                      }}
                    />
                  </Box>
                )}
                {onSelectionChange && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => handleVehicleToggle(vehicle, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        },
                      }}
                    />
                  </Box>
                )}
                <VehicleCard
                  vehicle={vehicle}
                  height="100%"
                  onTitleClick={onViewVehicle}
                  onManagePhotos={onManagePhotos}
                  customContent={
                    <Box sx={{ mt: 1 }}>
                      {/* Pickup Location Section - Prominent */}
                      <Box sx={{ 
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: 1,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        backgroundColor: 'primary.50',
                      }}>
                        <LocationOn sx={{ color: 'primary.main', fontSize: '1.4rem', mt: 0.15 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.dark', display: 'block', mb: 0.25 }}>
                            PICKUP LOCATION
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                            {pickupLabel || 'No pickup location set'}
                          </Typography>
                          {vehicle.pickup_location?.contact_name && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              Contact: {vehicle.pickup_location.contact_name}
                              {vehicle.pickup_location.contact_phone && ` - ${vehicle.pickup_location.contact_phone}`}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Paid Status Section */}
                      <Box
                        sx={{
                          mb: 1.5,
                          p: 1.25,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Chip
                            size="small"
                            color={vehicle.paid_for ? 'success' : 'warning'}
                            label={vehicle.paid_for ? 'Paid' : 'Not paid'}
                            sx={{ fontWeight: 600 }}
                          />
                          {vehicle.paid_for && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(vehicle.paid_for_date) || 'Date not set'}
                            </Typography>
                          )}
                        </Box>
                        {readyForTransport?.ready_for_pickup && onUnmarkReadyForPickup && (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            fullWidth
                            sx={{ mt: 1 }}
                            onClick={(event) => {
                              event.stopPropagation()
                              onUnmarkReadyForPickup(vehicle)
                            }}
                          >
                            Unmark ready for pickup
                          </Button>
                        )}
                      </Box>

                      {/* Mechanical and Body Status - only show for repair vehicles */}
                      {readyForTransport?.repair && (vehicle.mechanical_status === 'Need' || vehicle.body_status === 'Need') && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                          {vehicle.mechanical_status === 'Need' && (
                            <Chip
                              label="Mechanical"
                              size="small"
                              color="warning"
                              sx={{
                                fontSize: '0.7rem',
                                height: 24,
                                fontWeight: 500,
                              }}
                            />
                          )}
                          {vehicle.body_status === 'Need' && (
                            <Chip
                              label="Body"
                              size="small"
                              color="warning"
                              sx={{
                                fontSize: '0.7rem',
                                height: 24,
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Box>
                      )}

                    {/* Note if present */}
                    {readyForTransport?.note && (
                      <Box sx={{ 
                        mt: 1, 
                        p: 1, 
                        backgroundColor: 'grey.100', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
                          Note:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                          {readyForTransport.note}
                        </Typography>
                      </Box>
                    )}

                  </Box>
                }
                sx={{
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                  border: isSelected ? '2px solid' : '1px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                }}
              />
              </Box>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}

export default ReadyForTransportCards
